import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, NotebookPen, Pencil } from 'lucide-react'
import LinkifiedText from '../LinkifiedText'
import { useT } from '../../i18n/useT'

/**
 * Bottom-sheet per editare il campo "note" del task.
 * Comportamento (analogo al vecchio SmartNotes inline):
 *  - Note vuote      → textarea direttamente
 *  - Note esistenti  → preview con LinkifiedText (indirizzi → Google Maps,
 *                       checkbox `- [x]` toggleabili) + bottone "Modifica"
 *                       per passare alla textarea.
 * Tiene una copia locale (`draft`) per propagare al form parent solo su Save.
 *
 * Props:
 *   value    — string corrente
 *   onChange — (newVal) => void  (chiamato solo su Salva)
 *   onClose  — () => void
 */
export default function NotesEditorSheet({ value, onChange, onClose }) {
  const t = useT()
  const [draft, setDraft] = useState(value ?? '')
  // Modalità: 'preview' (default se draft non vuoto) | 'edit'
  const [mode, setMode] = useState(value?.trim() ? 'preview' : 'edit')
  const taRef = useRef(null)

  useEffect(() => {
    if (mode !== 'edit') return
    const ta = taRef.current
    if (!ta) return
    const len = ta.value.length
    ta.focus()
    try { ta.setSelectionRange(len, len) } catch {}
  }, [mode])

  const handleSave = () => { onChange(draft); onClose() }

  // Toggle checkbox `- [x]` inline (preview mode). Replica la logica del
  // vecchio SmartNotes: muta draft locale, ma NON salva — l'utente deve
  // ancora confermare con "Salva". Più sicuro: evita save involontari sul
  // testo libero quando l'intento era solo (s)pingere una checkbox.
  const toggleLine = (lineIdx, isDone) => {
    const lines = draft.split('\n')
    lines[lineIdx] = isDone
      ? lines[lineIdx].replace(/^- \[x\] /, '- ')
      : lines[lineIdx].replace(/^[-*]\s+/, '- [x] ')
    setDraft(lines.join('\n'))
  }

  const renderPreview = () =>
    draft.split('\n').map((line, i) => {
      const done = line.match(/^- \[x\] (.*)/)
      const todo = !done && line.match(/^[-*]\s+(.*)/)
      if (done) return (
        <div key={i} className="sn-item">
          <input type="checkbox" className="sn-check" checked readOnly
            onClick={(e) => { e.stopPropagation(); toggleLine(i, true) }} />
          <LinkifiedText as="span" className="sn-label sn-done" text={done[1]} />
        </div>
      )
      if (todo) return (
        <div key={i} className="sn-item">
          <input type="checkbox" className="sn-check" checked={false} readOnly
            onClick={(e) => { e.stopPropagation(); toggleLine(i, false) }} />
          <LinkifiedText as="span" className="sn-label" text={todo[1]} />
        </div>
      )
      return line
        ? <LinkifiedText as="p" key={i} className="sn-text" text={line} />
        : <br key={i} />
    })

  return createPortal(
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-sheet" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        <div className="modal-header">
          <span className="modal-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <NotebookPen size={18} /> {t('modal_notes')}
          </span>
          <button className="btn-icon" onClick={onClose} aria-label={t('close')}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body" style={{ paddingTop: 8 }}>
          {mode === 'preview' ? (
            <>
              <div className="sn-preview sn-preview--sheet">
                {renderPreview()}
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ width: '100%', marginTop: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onClick={() => setMode('edit')}
              >
                <Pencil size={14} /> {t('modal_notes_edit')}
              </button>
            </>
          ) : (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <textarea
                ref={taRef}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder={t('modal_notes_ph')}
                rows={8}
                maxLength={1000}
                style={{ resize: 'none' }}
              />
            </div>
          )}
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: 12 }}
            onClick={handleSave}
          >
            {t('notes_save')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
